import React, { useEffect, useState } from "react";
import CustomButton from "components/common/Button/CustomButton";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import { toast } from "react-toastify";
import { apiCall } from "store/services/service";
import {
  baseURLImage,
  getUrlRole,
  getFileNameValidationError,
  sanitizeDocumentUrl,
} from "Utils/utils";
import { apiGetRequest, apiPutRequest } from "helpers/Requests";
import Select from "../../../../../components/common/Select/Select";
import FileUploadInput from "components/common/FileUpload/FileUploadInput";
import { roleAccess } from "helpers/RolesPermission";
import { USER_ROLE, DOCUMENT_CONSTANTS } from "Constants/constant";
import { BASE_URL } from "store/services/URL";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import moment from "moment";
import { connect } from "react-redux";
import { getUser } from "../../../../../store/selector/auth.selector";

/** Parse date-only (YYYY-MM-DD) or Date to local midnight Date for picker/validation */
const toLocalDate = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
};

const toExpiryIso = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    const dateOnly = value.trim().substring(0, 10);
    const year = Number(dateOnly.slice(0, 4));
    if (year < 1900 || year > 9999) return null;
    return `${dateOnly}T00:00:00.000Z`;
  }
  const parsed = moment(value);
  if (!parsed.isValid()) return null;
  const year = parsed.year();
  if (year < 1900 || year > 9999) return null;
  return `${parsed.format("YYYY-MM-DD")}T00:00:00.000Z`;
};

function EditModel(props) {
  const {
    handleClose,
    setUpdatedFile,
    isOpen,
    uuid,
    currentFile,
    role,
    parentId,
    workerId,
    currentLoginUser,
    isUserGracePeriodOver,
    onModalClose,
  } = props;

  const [fileName, setFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDocTypeLoader, setIsDocTypeLoader] = useState(false);
  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [visibility, setVisibility] = useState(true);
  const [showInput, setShowInput] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  const [file, setFile] = useState(null);
  const [isFileUploadToS3, setIsFileUploadToS3] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [hasSetInitialCategory, setHasSetInitialCategory] = useState(false);
  const [isFileValid, setIsFileValid] = useState(true);
  const userType = localStorage.getItem("userType");

  const existingFileLink = currentFile?.link
    ? sanitizeDocumentUrl(baseURLImage(currentFile.link))
    : null;

  useEffect(() => {
    if (isOpen && currentFile) {
      setFileName(currentFile?.name ?? currentFile?.link);
      setFile(null);
      setIsFileUploadToS3(false);
      setIsLoading(false);

      if (typeof currentFile?.with_expiry !== "undefined") {
        setShowInput(Boolean(currentFile.with_expiry));
      } else if (currentFile?.expiry_time) {
        setShowInput(true);
      } else {
        setShowInput(false);
      }

      if (currentFile?.expiry_time) {
        const raw = String(currentFile.expiry_time).trim();
        const dateOnly = /^\d{4}-\d{2}-\d{2}/.test(raw)
          ? raw.substring(0, 10)
          : moment.utc(raw).format("YYYY-MM-DD");
        setExpiryDate(dateOnly);
      } else {
        setExpiryDate(null);
      }

      setVisibility(
        typeof currentFile?.visibility === "undefined"
          ? true
          : currentFile.visibility
      );

      if (
        !hasSetInitialCategory &&
        currentFile?.document_type_id &&
        fileTypeOptions.length > 0
      ) {
        const foundType = fileTypeOptions.find(
          (type) => type.value === currentFile.document_type_id
        );
        setSelectedFileType(foundType || null);
        setHasSetInitialCategory(true);
      }
    }
  }, [isOpen, currentFile, fileTypeOptions]);

  useEffect(() => {
    if (!isOpen) {
      setHasSetInitialCategory(false);
    }
  }, [isOpen]);

  const modalFooter = () => (
    <>
      <CustomButton
        label='Cancel'
        variant='secondary'
        clickHandler={() => {
          handleClear();
          setFile("");
          setSelectedDocument("");
          setShowInput(false);
          setFileName("");
          handleClose(false);
        }}
      />
      <CustomButton
        showLoading={isLoading}
        isDisabled={
          isLoading ||
          !isFileValid ||
          isUserGracePeriodOver ||
          !!getFileNameValidationError(fileName?.trim())
        }
        label='Update'
        variant='primary'
        clickHandler={handleFileUpdate}
      />
    </>
  );

  const getFileTypes = async () => {
    try {
      setIsDocTypeLoader(true);
      const res = await apiGetRequest(`${getUrlRole()}/doc-types`);
      if (res.status === 200) {
        const options = res.data.doc_types.map((doc) => {
          const obj = {
            label: doc.name,
            value: doc.id,
          };
          return obj;
        });
        setFileTypeOptions([...options]);
      }
      setIsDocTypeLoader(false);
    } catch (err) {
      console.log("err", err);
      setIsDocTypeLoader(false);
    }
  };

  useEffect(() => {
    getFileTypes();
    if (role === "sil") {
      setSelectedFileType({ label: "Sil Documents", value: 2 });
    }
  }, []);

  const handleClear = () => {
    setVisibility(true);
    setSelectedFileType("");
    setFile("");
    setFileName("");
    setExpiryDate(null);
    setShowInput(false);
    setIsFileValid(true);
  };

  const handleFinish = (data) => {
    setIsLoading(false);
    setIsFileUploadToS3(false);
    if (data) {
      if (role !== "sil" && data.data && data.data.file_type) {
        setUpdatedFile({ data });
      }
      handleClose(false);
      if (typeof onModalClose === "function") {
        onModalClose();
      }
    }
    setVisibility(true);
    handleClear();
    setFile(null);
    setSelectedDocument("");
    setFileName("");
    toast.success("Document updated successfully");
  };

  const handleError = () => {
    setIsLoading(false);
    setIsFileUploadToS3(false);
  };

  const buildUpdatePayload = (extra = {}) => {
    const payload = {
      name: (fileName || currentFile?.name || currentFile?.link || "").trim(),
      document_type_id: selectedFileType?.value,
      with_expiry: !!showInput,
      expiry_time: showInput ? toExpiryIso(expiryDate) : null,
      folder_structure_id: parentId ?? null,
      visibility: visibility,
      ...extra,
    };
    if (role === "sil") {
      payload.sil_home_id = workerId;
    } else {
      payload.worker_id = workerId;
    }
    if (role === "participant" && userType === "sp_admin") {
      payload.visibility = visibility;
    }
    return payload;
  };

  const submitSilDocumentUpdate = async (data) => {
    if (!uuid) {
      setIsLoading(false);
      setIsFileUploadToS3(false);
      toast.error("Missing document id. Please close and try again.");
      return;
    }
    try {
      const response = await apiPutRequest(
        `sil/home/document/update-document/${uuid}`,
        data
      );
      handleFinish(response?.data ?? response);
      setShowInput(false);
    } catch (e) {
      setIsLoading(false);
      setIsFileUploadToS3(false);
      toast.error(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Something Went Wrong!"
      );
    }
  };

  const handleFileUploadBaseVapour = async (data) => {
    const nameError = getFileNameValidationError(fileName?.trim());
    if (nameError) {
      toast.error(nameError);
      setIsLoading(false);
      setIsFileUploadToS3(false);
      return;
    }
    if (file instanceof File && file.size > DOCUMENT_CONSTANTS.MAX_FILE_SIZE_BYTES) {
      toast.error(
        `File size exceeds ${DOCUMENT_CONSTANTS.MAX_FILE_SIZE_MB} MB. Please upload a smaller file.`
      );
      setIsLoading(false);
      setIsFileUploadToS3(false);
      return;
    }

    const payload = buildUpdatePayload({
      name: fileName || currentFile?.link,
      file_type: data.ext,
      key: data.key,
      ext: data.ext,
      label: "",
      note: "xyz",
    });

    if (role === "sil") {
      await submitSilDocumentUpdate(payload);
      return;
    }

    const Role = roleAccess([USER_ROLE.Sp_Standard_User]) ? "hrm" : "sp";
    const URL = `${
      BASE_URL + Role
    }/file-management/update-worker-document/${uuid}`;
    const URLNew = `${BASE_URL + Role}/file-management/update-document/${uuid}`;

    apiCall
      .put({
        url: role === "participant" ? URLNew : URL,
        params: payload,
        isAuthToken: currentLoginUser?.AccessToken,
        skipQueryParams: true,
      })
      .then((response) => {
        handleFinish(response);
        setShowInput(false);
      })
      .catch((e) => {
        console.log("e", e);
        setIsLoading(false);
        setIsFileUploadToS3(false);
        toast.error("Something Went Wrong!");
      });
  };

  const handleFileUpdate = async () => {
    if (isUserGracePeriodOver) return;

    const nameError = getFileNameValidationError(fileName?.trim());
    if (nameError) {
      toast.error(nameError);
      return;
    }

    if (showInput) {
      const iso = toExpiryIso(expiryDate);
      if (!iso) {
        toast.error(
          "Please enter a valid expiry date in DD/MM/YYYY format."
        );
        return;
      }
      const expiry = moment(iso);
      if (
        !expiry.isValid() ||
        expiry.startOf("day").isBefore(moment().startOf("day"))
      ) {
        toast.error("Expiry date must be a future date.");
        return;
      }
    }

    setIsLoading(true);

    // Only a real newly selected File should go through S3 vapour upload.
    // Otherwise metadata-only update (name / expiry) — avoids infinite loading.
    if (file instanceof File) {
      setIsFileUploadToS3(true);
      return;
    }

    const data = buildUpdatePayload();

    if (role === "sil") {
      await submitSilDocumentUpdate(data);
      return;
    }

    data.worker_id = workerId;

    const Role = roleAccess([USER_ROLE.Sp_Standard_User]) ? "hrm" : "sp";
    const URL = `${
      BASE_URL + Role
    }/file-management/update-worker-document/${uuid}`;
    const URLNew = `${BASE_URL + Role}/file-management/update-document/${uuid}`;

    apiCall
      .put({
        url: role === "participant" ? URLNew : URL,
        params: data,
        isAuthToken: currentLoginUser?.AccessToken,
        skipQueryParams: true,
      })
      .then((response) => {
        handleFinish(response);
        setShowInput(false);
      })
      .catch((e) => {
        console.log("e", e);
        setIsLoading(false);
        toast.error("Something Went Wrong!");
      });
  };

  return (
    <>
      <ModalSkeleton
        title='Edit File'
        cssClass='w-[35%]'
        modalFooter={modalFooter()}
        isOpen={isOpen}
        closeModal={() => {
          setVisibility(true);
          handleClose(false);
          handleClear();
          setShowInput(false);
          setFile("");
          setSelectedDocument("");
          setFileName("");
        }}
      >
        <div className='flex flex-col'>
          <div className='grid grid-cols-2 gap-x-4'>
            <div className='col-span-2'>
              <div className='space-y-4'>
                <div>
                  <label
                    htmlFor='first_name'
                    className='text-start did-floating-label'
                  >
                    File Name
                  </label>
                  <input
                    type='text'
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    className='did-floating-input capitalize'
                  />
                  {getFileNameValidationError(fileName?.trim()) && (
                    <span className='text-sm text-red-500 mt-1 block'>
                      {getFileNameValidationError(fileName.trim())}
                    </span>
                  )}
                </div>
                {role !== "sil" && (
                  <div className='mt-4 flex items-start justify-start space-x-2'>
                    <div className='did-floating-label-content w-full'>
                      <label
                        htmlFor='interview-questions'
                        className=' text-start did-floating-label'
                      >
                        Category
                      </label>
                      <Select
                        isDisabled={isDocTypeLoader}
                        loading={isDocTypeLoader}
                        cssClass='z-20'
                        isSearch={false}
                        data={fileTypeOptions}
                        selectedOption={selectedFileType}
                        handleChange={(e) => setSelectedFileType(e)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {role === "participant" && (
              <div className='col-span-2 space-x-4'>
                <label className=' text-start did-floating-label'>
                  Show to Worker
                </label>
                <input
                  type='checkbox'
                  checked={!visibility}
                  onClick={() => setVisibility(!visibility)}
                  onChange={() => {}}
                  className='rounded '
                />
              </div>
            )}
            <div className='col-span-2 space-x-4'>
              <label className=' text-start did-floating-label'>
                Set Expiry Date
              </label>
              <input
                type='checkbox'
                checked={showInput}
                onClick={() => setShowInput(!showInput)}
                onChange={() => {}}
                className='rounded '
              />
            </div>

            {showInput && (
              <div className='col-span-2'>
                <label
                  htmlFor='expiry_date'
                  className='text-start did-floating-label'
                >
                  Expiry Date
                </label>
                <DateRangePickerNew
                  isSingleDate
                  isClearable={false}
                  handleCancel={() => setExpiryDate(null)}
                  showCalendarIcon
                  preventOpenOnFocus
                  handleChange={(obj) => {
                    setExpiryDate(obj);
                  }}
                  singleSelectedDate={
                    expiryDate ? toLocalDate(expiryDate) : null
                  }
                  minDate={moment().startOf("day").toDate()}
                />
              </div>
            )}

            {!selectedDocument && (
              <div className='col-span-2 mt-4'>
                <FileUploadInput
                  type='any'
                  handleFile={(e) => {
                    const maxSizeMB = DOCUMENT_CONSTANTS.MAX_FILE_SIZE_MB;
                    const maxSizeBytes = DOCUMENT_CONSTANTS.MAX_FILE_SIZE_BYTES;
                    if (e.size > maxSizeBytes) {
                      toast.error(
                        `File size should not exceed ${maxSizeMB} MB.`
                      );
                      setIsFileValid(false);
                      return;
                    }
                    setIsFileValid(true);
                    setFile(e);
                  }}
                  size={String(DOCUMENT_CONSTANTS.MAX_FILE_SIZE_MB)}
                  file={file}
                  handleDelete={() => {
                    setFile(null);
                    setIsFileValid(true);
                  }}
                  isDisabled={isLoading}
                  isVapourFileUpload={true}
                  isFileUploadToS3={isFileUploadToS3}
                  handleFileUploadBaseVapour={handleFileUploadBaseVapour}
                  handleError={handleError}
                  updateFileName={
                    existingFileLink
                      ? currentFile?.name || currentFile?.link
                      : null
                  }
                  selectedFile={
                    existingFileLink ? { link: existingFileLink } : null
                  }
                />
              </div>
            )}
          </div>
        </div>
      </ModalSkeleton>
    </>
  );
}

const mapStateToProps = (state) => {
  const currentLoginUser = getUser(state);

  return { currentLoginUser };
};

export default connect(mapStateToProps, null)(EditModel);
